// Render a delivery instant as dd/mm/aaaa · HH:MM in the locker's local timezone (ADR
// 0011), not the browser's — reducing an instant to a wall-clock day and time is
// timezone-dependent (a parcel stored at 23:30 UTC is already the next day, past
// midnight, in Madrid). Date and time are formatted separately so the join is a middot,
// not the locale's default date-time separator; hourCycle 'h23' keeps midnight as 00:00.
// An absent date shows an em dash; an unparseable value — or an unusable TIMEZONE — falls back to
// its raw string. Absent means
// `null` OR blank: the building-wide delivery list normalizes a delivered `stored_date: null` to
// `''` (the shared `Delivery` type declares `storedDate` a string), and a blank rendered as itself
// paints an empty cell — which reads as a rendering bug rather than as «this parcel never reached a
// box». One em dash for both, so the cell agrees with the pickup column beside it.
export function formatDeliveryDateTime(iso: string | null, timeZone: string): string {
  if (iso === null || iso.trim() === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  // `Intl` throws `RangeError` on any zone it does not recognise — the empty string and a padded
  // name included — and NO caller can guarantee a valid IANA name: the reads that pass a Zod-parsed
  // `timezone` declare it `z.string()`, which accepts «Nowhere/Nothing» just as happily. This
  // formatter is reached from the «Entrega» detail, both delivery lists and the tracking modal, and
  // the last of those renders in the header, OUTSIDE the shell's only error boundary — so one bad
  // zone in one payload used to take the panel down from inside a render.
  //
  // The fallback is the one already used for an unparseable date: the raw instant. Visibly wrong
  // beats a crash, and beats the alternative of quietly formatting in some other zone — which is
  // the thing ADR 0011 exists to prevent, and a wrong answer wearing the clothes of a right one.
  try {
    const day = new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    }).format(date);
    const time = new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).format(date);
    return `${day} · ${time}`;
  } catch {
    return iso;
  }
}
